import type { SkuSaleWindow, SkuSaleWindowInput } from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  ScheduleWindowsEditor,
  type ScheduleWindowsCopy,
} from '../schedule-windows/ScheduleWindowsEditor.tsx';
import {
  draftsToScalarInputs,
  scalarWindowToDraft,
  type WeekdayKey,
} from '../schedule-windows/scheduleWindowDraft.ts';

const SALE_COPY: ScheduleWindowsCopy = {
  none: 'sku_detail.pricing.windows.none',
  active: 'sku_detail.pricing.windows.active',
  base: 'sku_detail.pricing.windows.base',
  effective: 'sku_detail.pricing.windows.effective',
  configure: 'sku_detail.pricing.windows.configure',
  dialogTitle: 'sku_detail.pricing.windows.dialog_title',
  dialogDescription: 'sku_detail.pricing.windows.dialog_description',
  close: 'sku_detail.pricing.windows.close',
  cancel: 'sku_detail.pricing.windows.cancel',
  save: 'sku_detail.pricing.windows.save',
  add: 'sku_detail.pricing.windows.add',
  remove: 'sku_detail.pricing.windows.remove',
  empty: 'sku_detail.pricing.windows.empty',
  enabled: 'sku_detail.pricing.windows.enabled',
  label: 'sku_detail.pricing.windows.label',
  timezone: 'sku_detail.pricing.windows.timezone',
  days: 'sku_detail.pricing.windows.days',
  day: {
    mon: 'sku_detail.pricing.windows.day.mon',
    tue: 'sku_detail.pricing.windows.day.tue',
    wed: 'sku_detail.pricing.windows.day.wed',
    thu: 'sku_detail.pricing.windows.day.thu',
    fri: 'sku_detail.pricing.windows.day.fri',
    sat: 'sku_detail.pricing.windows.day.sat',
    sun: 'sku_detail.pricing.windows.day.sun',
  } satisfies Record<WeekdayKey, I18nKey>,
  start: 'sku_detail.pricing.windows.start',
  end: 'sku_detail.pricing.windows.end',
  crossMidnight: 'sku_detail.pricing.windows.cross_midnight',
  multiplier: 'sku_detail.pricing.windows.multiplier',
  labelRequired: 'sku_detail.pricing.windows.label_required',
  timezoneRequired: 'sku_detail.pricing.windows.timezone_required',
  daysRequired: 'sku_detail.pricing.windows.days_required',
  clockInvalid: 'sku_detail.pricing.windows.clock_invalid',
  startEndEqual: 'sku_detail.pricing.windows.start_end_equal',
  startNotEndOfDay: 'sku_detail.pricing.windows.start_not_end_of_day',
  multiplierInvalid: 'sku_detail.pricing.windows.multiplier_invalid',
};

export function SkuSaleWindowsEditor({
  skuId,
  skuName,
  rateMultiplier,
  windows,
  activeWindowId,
  effectiveRateMultiplier,
  saving,
  onSave,
}: {
  readonly skuId: string;
  readonly skuName: string;
  readonly rateMultiplier: number;
  readonly windows: readonly SkuSaleWindow[];
  readonly activeWindowId: string | null;
  readonly effectiveRateMultiplier: number;
  readonly saving: boolean;
  readonly onSave: (windows: readonly SkuSaleWindowInput[]) => Promise<void> | void;
}): JSX.Element {
  const activeWindow =
    activeWindowId === null
      ? undefined
      : windows.find((window) => window.window_id === activeWindowId);

  return (
    <ScheduleWindowsEditor
      testIdPrefix="sku-sale-windows"
      testIdWindowPrefix="sku-sale-window"
      subjectKey={skuId}
      titleParams={{ model: skuName }}
      copy={SALE_COPY}
      baseMultiplier={rateMultiplier}
      effectiveMultiplier={effectiveRateMultiplier}
      activeLabel={activeWindow?.label}
      loaded
      available
      saving={saving}
      showDimensions={false}
      loadDrafts={() =>
        windows.map((window, index) =>
          scalarWindowToDraft(
            {
              window_id: window.window_id,
              label: window.label,
              timezone: window.timezone,
              days_of_week: window.days_of_week,
              start_minute: window.start_minute,
              end_minute: window.end_minute,
              multiplier: window.sale_multiplier,
              enabled: window.enabled,
            },
            `existing-${index}`,
          ),
        )
      }
      parseDrafts={draftsToScalarInputs}
      onSave={async (drafts) => {
        const parsed = draftsToScalarInputs(drafts);
        if (parsed === null) return;
        await onSave(
          parsed.map((window) => ({
            ...(window.window_id === undefined ? {} : { window_id: window.window_id }),
            label: window.label,
            timezone: window.timezone,
            days_of_week: window.days_of_week,
            start_minute: window.start_minute,
            end_minute: window.end_minute,
            sale_multiplier: window.multiplier,
            enabled: window.enabled,
          })),
        );
      }}
    />
  );
}
